import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject } from '@angular/core';
import { IApiResponse } from '@web/shared-types';
import { catchError, map, Observable, of, tap } from 'rxjs';
import { z } from 'zod/v4';
import { AppConfigService } from '../../services/app-config.service';
import { ErrorHandlingService } from '../../services/errorHandling/error-handling.service';
import { MessageService } from '../../services/messages/messages.service';
import { ValidatorService } from '../../services/validator/validator.service';
import { AuthProviderFactory } from '../../services/auth/auth-provider.factory';
import { AuthProvider } from '../../services/auth/auth.provider';
import { Router } from '@angular/router';
import { authClientForUrl } from '@web/shared-functions/auth';
import {
  CLIENT_CHANNEL_HEADER,
  EConfigurationChannel,
  EMessageType,
} from '@web/dtos';

export type RequestParams = {
  [param: string]:
    | string
    | number
    | boolean
    | ReadonlyArray<string | number | boolean>;
};

type ObservableKey = 'entity' | 'download';
export class RestCallBuilder<I, O = I, R = null> {
  readonly messageService: MessageService = inject(MessageService);
  private readonly configService: AppConfigService = inject(AppConfigService);
  private http: HttpClient = inject(HttpClient);
  private errorHandlingService: ErrorHandlingService =
    inject(ErrorHandlingService);
  private validatorService: ValidatorService = inject(ValidatorService);
  private authFactory: AuthProviderFactory = inject(AuthProviderFactory);
  private readonly router: Router = inject(Router);

  private get auth(): AuthProvider {
    return this.authFactory.getProvider(authClientForUrl(this.router.url));
  }

  private apiUrl: string | undefined = this.configService.get().general.cmsUrl;

  private readonly observables: Record<
    ObservableKey,
    Observable<IApiResponse<any>>
  >;
  private readonly defaultEntity: O;

  constructor(defaultEntity: O, downloadContentEntity?: R) {
    this.observables = {
      entity: of({ data: defaultEntity }),
      download: of({ data: downloadContentEntity }),
    };
    this.defaultEntity = defaultEntity;
  }

  getExternal(url: string, params?: RequestParams) {
    const headers = this.createAuthorizationHeader({
      clientId: this.configService.get().client.id,
    });
    this.observables.entity = this.http.get<IApiResponse<O>>(url, {
      headers: headers,
      params,
    });
    return this;
  }

  putExternal(url: string, body: I, params?: RequestParams) {
    this.observables.entity = this.http.put<IApiResponse<O>>(url, body, {
      headers: this.createAuthorizationHeader({
        clientId: this.configService.get().client.id,
      }),
      params,
    });
    return this;
  }

  postExternal(url: string, body: I, params?: RequestParams) {
    this.observables.entity = this.http.post<IApiResponse<O>>(url, body, {
      headers: this.createAuthorizationHeader({
        clientId: this.configService.get().client.id,
      }),
      params,
    });
    return this;
  }

  deleteExternal(url: string, params?: RequestParams) {
    this.observables.entity = this.http.delete<IApiResponse<O>>(url, {
      headers: this.createAuthorizationHeader({
        clientId: this.configService.get().client.id,
      }),
      params,
    });
    return this;
  }

  get(url: string, params?: RequestParams) {
    this.observables.entity = this.http.get<IApiResponse<O>>(
      this.apiUrl + url,
      {
        headers: this.createAuthorizationHeader(),
        params,
      }
    );
    return this;
  }

  getDownloadableContent(url: string, params?: RequestParams) {
    this.observables.download = this.http.get<IApiResponse<R>>(
      this.apiUrl + url,
      {
        headers: this.createAuthorizationHeader(),
        params,
      }
    );
    return this;
  }

  getBlob(url: string): Observable<Blob> {
    return this.http.get(this.apiUrl + url, {
      headers: this.createAuthorizationHeader(),
      responseType: 'blob',
    });
  }

  post(url: string, query: I | FormData) {
    this.observables.entity = this.http.post<IApiResponse<O>>(
      this.apiUrl + url,
      query,
      {
        headers: this.createAuthorizationHeader(),
      }
    );
    return this;
  }

  patch(url: string, query: I | FormData) {
    this.observables.entity = this.http.patch<IApiResponse<O>>(
      this.apiUrl + url,
      query,
      {
        headers: this.createAuthorizationHeader(),
      }
    );
    return this;
  }

  upload(url: string, query: I | FormData) {
    this.observables.entity = this.http.post<IApiResponse<O>>(
      this.apiUrl + url,
      query,
      {
        headers: this.createAuthorizationHeader(),
      }
    );
    return this;
  }

  delete(url: string) {
    this.observables.entity = this.http.delete<IApiResponse<O>>(
      this.apiUrl + url,
      {
        headers: this.createAuthorizationHeader(),
      }
    );
    return this;
  }

  validate(schema: z.ZodType<IApiResponse<O>>, context: string) {
    this.observables.entity = this.observables.entity?.pipe(
      map(
        (entity: IApiResponse<O>): IApiResponse<O> =>
          this.validatorService.validate(entity, schema, context)
      )
    );
    return this;
  }

  handleMessages(messageTypes?: readonly EMessageType[]) {
    return this.handleMessagesFor('entity', this.defaultEntity, messageTypes);
  }

  handleMessagesDownloadContent() {
    return this.handleMessagesFor('download', {} as R);
  }

  build(): Observable<IApiResponse<O>> {
    return this.observables.entity;
  }

  buildDownloadContent() {
    return this.observables.download;
  }

  private handleMessagesFor<T>(
    key: ObservableKey,
    defaultData: T,
    messageTypes?: readonly EMessageType[]
  ): this {
    this.observables[key] = this.observables[key].pipe(
      tap((response: IApiResponse<T>) => {
        if (
          response?.message &&
          (!messageTypes || messageTypes.includes(response.message.type))
        ) {
          this.messageService.add(response.message);
        }
      }),
      catchError(err => {
        console.error(err);
        return this.errorHandlingService.handle<IApiResponse<T>>(err, {
          data: defaultData,
        });
      })
    );
    return this;
  }

  private createAuthorizationHeader(additionalHeaders?: {
    [key: string]: string;
  }): HttpHeaders {
    const headers: Record<string, string> = {
      [CLIENT_CHANNEL_HEADER]: EConfigurationChannel.Web,
      ...additionalHeaders,
    };

    if (this.auth.isLoggedIn()) {
      headers['Authorization'] = `Bearer ${this.auth.getAccessToken()}`;
    }

    return new HttpHeaders(headers);
  }
}
